import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import BoxHolder from '../../components/shared/BoxHolder/BoxHolder';
import Badge from '../../components/shared/Badge/Badge';
import Loading from '../../components/shared/Loading';
import BtButton from '../bt-button/bt-button';
import { RequestStatus, serviceEvaluations } from '../../services/service-evaluations';
import { EvaluationRecipientStatus } from '../../util/evaluationContract';
import { PROGRESS_BAR, requestStatusMeta } from '../../util/evaluationStatusMeta';
import EvaluationsLayout from './EvaluationsLayout';
import StatusTiles from './StatusTiles';
import RecipientTable, { RecipientRow } from './RecipientTable';
import ResponseModal from './ResponseModal';
import { formatDayKey } from './reviewFormat';
import { reportUnexpected } from './reportUnexpected';
import styles from './page-supplier-evaluations.module.scss';

type Props = {
  requestId: string;
  registerUrl: string;
  reviewUrlOf: (supplierId: string) => string;
};

type LoadFailure = 'notFound' | 'failed';

const POLL_INTERVAL_MS = 5000;
const POLL_WINDOW_MS = 2 * 60 * 1000;
const MAX_POLLS = 24;

const mergeRecipients = (current: RecipientRow[], incoming: RecipientRow[]): RecipientRow[] => {
  const incomingById = new Map(incoming.map(recipient => [recipient._id, recipient]));
  const known = new Set(current.map(recipient => recipient._id));
  return [
    ...current.map(recipient => incomingById.get(recipient._id) ?? recipient),
    ...incoming.filter(recipient => !known.has(recipient._id)),
  ];
};

const isStillSending = (status: RequestStatus) =>
  status.recipients.some(recipient => recipient.status === EvaluationRecipientStatus.NotSent) &&
  Date.now() - Date.parse(status.request.createdAt) < POLL_WINDOW_MS;

const RequestStatusPage: FC<Props> = ({ requestId, registerUrl, reviewUrlOf }) => {
  const [t, i18n] = useTranslation('evaluations');
  const history = useHistory();
  const [status, setStatus] = useState<RequestStatus | null>(null);
  const [failure, setFailure] = useState<LoadFailure | null>(null);
  const [openResponseId, setOpenResponseId] = useState<string | null>(null);
  const polls = useRef(0);

  const fetchStatus = useCallback(async () => {
    const next = await serviceEvaluations.getRequestStatus(requestId);
    setStatus(previous =>
      previous
        ? { ...next, recipients: mergeRecipients(previous.recipients, next.recipients) }
        : next,
    );
  }, [requestId]);

  const load = useCallback(async () => {
    try {
      await fetchStatus();
      setFailure(null);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setFailure('notFound');
        return;
      }
      reportUnexpected(error);
      setFailure('failed');
    }
  }, [fetchStatus]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!status || polls.current >= MAX_POLLS || !isStillSending(status)) return undefined;
    const timer = window.setTimeout(() => {
      polls.current += 1;
      fetchStatus().catch(reportUnexpected);
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [status, fetchStatus]);

  const retry = () => {
    setFailure(null);
    setStatus(null);
    load();
  };

  const request = status?.request;
  const path = [request?.supplier.name, request?.title].filter((segment): segment is string =>
    Boolean(segment),
  );
  const backTo = request ? reviewUrlOf(request.supplier._id) : registerUrl;

  const renderContent = () => {
    if (failure === 'notFound') {
      return (
        <p className={styles.loadError} role="alert">
          {t('status.notFound', 'Utvärderingen hittades inte.')}
        </p>
      );
    }
    if (failure) {
      return (
        <div className={styles.loadError} role="alert">
          <span>{t('status.loadFailed', 'Utvärderingen kunde inte hämtas.')}</span>
          <BtButton size="xxs" onClick={retry}>
            {t('review.retry', 'Försök igen')}
          </BtButton>
        </div>
      );
    }
    if (!status || !request) return <Loading type="inline" />;

    const { context, supplier } = request;
    const { recipients, answered } = status.tiles;
    const project = [context.projectNumber, context.projectName].filter(Boolean).join(' ');
    const contextLabel =
      [project, context.areaName].filter(Boolean).join(' · ') ||
      t('status.context.none', 'Utan projekt');
    const requestMeta = requestStatusMeta(request.status);

    return (
      <>
        <BoxHolder className={styles.card}>
          <div className={styles.requestHeader}>
            <div className={styles.requestHeaderText}>
              <div className={styles.titleRow}>
                <h2 className={styles.entityTitle}>{request.title}</h2>
                <Badge color={requestMeta.color} backgroundColor={requestMeta.backgroundColor}>
                  {t(requestMeta.labelKey, requestMeta.fallback)}
                </Badge>
              </div>
              <p className={`${styles.metaLine} ${styles.muted}`}>
                {[
                  contextLabel,
                  t('status.dueDate', 'Sista svarsdag {{date}}', {
                    date: formatDayKey(request.dueDate, i18n.language),
                  }),
                ].join(' · ')}
              </p>
              {recipients > 0 && (
                <div className={styles.progress}>
                  <div className={styles.progressTrack} style={{ background: PROGRESS_BAR.track }}>
                    <div
                      className={styles.progressFill}
                      style={{
                        background: PROGRESS_BAR.fill,
                        width: `${Math.min(100, (answered / recipients) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className={`${styles.small} ${styles.muted}`}>
                    {t('status.answeredOf', '{{answered}} av {{total}} har svarat', {
                      answered,
                      total: recipients,
                    })}
                  </span>
                </div>
              )}
            </div>
            <BtButton
              color="white"
              onClick={() =>
                history.push(registerUrl, { q: supplier.organizationNumber || supplier.name })
              }
            >
              {t('status.supplierProfile', 'Leverantörsprofil')}
            </BtButton>
          </div>
        </BoxHolder>
        <StatusTiles tiles={status.tiles} />
        <BoxHolder className={styles.card}>
          <h3 className={styles.cardTitle}>{t('status.recipients.title', 'Mottagare')}</h3>
          <div className={styles.table}>
            <RecipientTable recipients={status.recipients} onShowAnswer={setOpenResponseId} />
          </div>
        </BoxHolder>
        {openResponseId && (
          <ResponseModal
            responseId={openResponseId}
            onClose={() => setOpenResponseId(null)}
            onHidden={load}
          />
        )}
      </>
    );
  };

  return (
    <EvaluationsLayout path={path} backTo={backTo}>
      {renderContent()}
    </EvaluationsLayout>
  );
};

export default RequestStatusPage;
